// @ts-nocheck - TODO: Fix TypeScript errors systematically

/**
 * Sidebar.tsx - Sidebar - Sidebar panel component
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState } from 'react';
import { usePanel, usePanelResize, usePanelr } from '../PanelrContext';
import { ContextMenu } from '@opcentral/navigatr';
import type { ContextMenuItem } from '@opcentral/navigatr';
import type { PanelMode } from '../PanelrManager';

interface SidebarProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
  position?: 'left' | 'right' | 'top' | 'bottom';
  mode?: PanelMode;
  widths?: {
    minimized: number;
    normal: number;
  };
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  collapsible?: boolean;
  persistent?: boolean;
  overlay?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onModeChange?: (mode: PanelMode) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  id,
  children,
  className = '',
  title,
  position = 'left',
  mode = 'normal',
  widths = { minimized: 60, normal: 280 },
  defaultWidth = 280,
  minWidth = 200,
  maxWidth = 500,
  resizable = true,
  collapsible = true,
  persistent = true,
  overlay = false,
  onOpen,
  onClose,
  onModeChange
}) => {
  const { state, isOpen, width, toggle } = usePanel(id, {
    position,
    mode,
    widths,
    defaultWidth,
    minWidth,
    maxWidth,
    resizable,
    collapsible,
    persistent
  });
  
  const { isDragging, handleMouseDown } = usePanelResize(id);
  const { panelr } = usePanelr();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showDevHelpers, setShowDevHelpers] = useState(false);
  const [visualSettings, setVisualSettings] = useState({ showBorders: false, colorCodeLevels: false });

  const currentMode = state?.mode || mode;
  
  // Debug logging
  console.log(`[Sidebar ${id}] Render - mode: ${currentMode}, state:`, state);
  
  // Update visual settings when menu opens
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const settings = panelr.getVisualSettings();
    setVisualSettings(settings);
    setContextMenu({ x: e.clientX, y: e.clientY });
  };
  
  // Build context menu items
  const contextMenuItems: ContextMenuItem[] = [
    {
      id: 'dev-helpers',
      label: 'Show Dev Helpers',
      type: 'submenu',
      submenu: [
        {
          id: 'show-borders',
          label: `${visualSettings.showBorders ? '✓' : '  '} Show Borders`,
          action: () => {
            const newValue = !visualSettings.showBorders;
            panelr.setShowBorders(newValue);
            setVisualSettings({ ...visualSettings, showBorders: newValue });
          }
        },
        {
          id: 'color-code',
          label: `${visualSettings.colorCodeLevels ? '✓' : '  '} Color Code Levels`,
          action: () => {
            const newValue = !visualSettings.colorCodeLevels;
            panelr.setColorCodeLevels(newValue);
            setVisualSettings({ ...visualSettings, colorCodeLevels: newValue });
          }
        },
        {
          id: 'show-info',
          label: 'Show Info (Console)',
          action: () => {
            const panel = panelr.getState(id);
            console.group(`[Panelr] Panel Info: ${id}`);
            console.log('State:', panel);
            console.log('All Panels:', panelr.getAllPanels());
            console.log('Content Area:', panelr.calculateContentArea());
            console.groupEnd();
          }
        }
      ]
    }
  ];
  
  // Don't render if invisible
  if (currentMode === 'invisible') {
    return null;
  }
  
  // Use CSS to hide when hidden or closed, but keep in DOM
  const isVisible = currentMode !== 'hidden' && isOpen;
  
  // Calculate width based on current mode
  let calculatedWidth = defaultWidth;
  if (currentMode === 'minimized' && widths?.minimized) {
    calculatedWidth = widths.minimized;
  } else if (currentMode === 'normal' && widths?.normal) {
    calculatedWidth = widths.normal;
  } else if (width) {
    calculatedWidth = width; // Use custom width if set
  }

  const sidebarStyle: React.CSSProperties = {
    width: `${calculatedWidth}px`,
    display: isVisible ? 'flex' : 'none'
  };

  const handleToggle = () => {
    toggle();
    if (isOpen) {
      onClose?.();
    } else {
      onOpen?.();
    }
  };

  return (
    <>
      {overlay && <div className="sidebar-overlay" onClick={handleToggle} />}
      
      <aside 
        className={`sidebar sidebar-${position} ${className} ${isDragging ? 'resizing' : ''}`}
        style={sidebarStyle}
        data-position={position}
        data-mode={currentMode}
        data-level={state?.level ?? 0}
        onContextMenu={handleContextMenu}
      >
        {title && (
          <div className="sidebar-header">
            <h3 className="sidebar-title">{title}</h3>
            {collapsible && (
              <button 
                className="btn btn-ghost btn-sm sidebar-close"
                onClick={handleToggle}
                aria-label="Close sidebar"
              >
                ✕
              </button>
            )}
          </div>
        )}
        
        <div className="sidebar-content">
          {children}
        </div>
        
        {resizable && (
          <div 
            className={`sidebar-resize-handle sidebar-resize-${position}`}
            onMouseDown={handleMouseDown}
            data-position={position}
          />
        )}
      </aside>
      
      <ContextMenu
        isOpen={!!contextMenu}
        position={contextMenu || { x: 0, y: 0 }}
        items={contextMenuItems}
        onClose={() => setContextMenu(null)}
        theme="dark"
      />
    </>
  );
};

export default Sidebar;
